<template>
  <div
    class="bg-white border-t-1 border-stone-100 mt-8 lg:mt-12 py-3 flex items-center"
    :class="{
      'sticky bottom-0 z-50': sticky,
    }"
  >
    <div
      class="container container-grid items-center"
      :style="{ '--col-left-span': leftColSpan }"
    >
      <div
        v-if="showLinkToFirst || showLinkToMultiple || $slots.default"
        ref="leftDiv"
        class="locations__col-span--left"
      >
        <slot>
          <DrupalLink
            v-if="firstLink && showLinkToFirst"
            :to="firstLink"
            class="button"
          >
            {{ $texts('exhibition.go_location', 'Besuchen') }}
          </DrupalLink>
          <a v-else-if="showLinkToMultiple" href="#locations" class="button">
            {{ $texts('exhibition.go_location', 'Besuchen') }}
          </a>
        </slot>
      </div>
      <div
        v-if="!hideLocation"
        :class="{
          'container-grid items-start': hasMultipleLocations,
        }"
        class="locations__col-span--right"
      >
        <div
          v-for="item in locations"
          :key="item.id"
          :class="{
            'hidden md:grid col-span-3': hasMultipleLocations,
          }"
        >
          <LocationTeaser
            v-if="item"
            v-bind="item"
            :show-link="showLink"
            :show-status="showStatus"
            class="md:text-base lg:text-lg"
          />
        </div>
        <div v-if="hasMultipleLocations" class="text-sm md:hidden col-span-9">
          <div class="font-bold leading-none mb-1">
            {{ locationsMerged }}
          </div>
          <div v-if="!showStatus && locations && locations.length > 1">
            <a class="link-underline" href="#locations">{{
              $texts('locationsBarMultiple', 'Zu den Standorten')
            }}</a>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="hasMultipleLocations && !hideLocation"
    id="locations"
    class="container my-6"
    :class="{ 'md:hidden': !showStatus }"
  >
    <h2 class="h2 section-title">
      {{ $texts('locationsTargetMultiple', 'Standorte') }}
    </h2>
    <div class="grid gap-4">
      <div v-for="item in locations" :key="'additional_' + item.id">
        <LocationTeaser
          v-if="item"
          v-bind="item"
          hide-image
          show-link
          :show-status="showStatus"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { LocationTeaserFragment } from '#graphql-operations'

const props = defineProps<{
  locations?: LocationTeaserFragment[]
  showLink?: boolean
  showStatus?: boolean
  sticky?: boolean
  hideLocation?: boolean
}>()

const leftDiv = ref<HTMLElement | null>(null)
const leftColSpan = ref(2)

// Compute the number of columns the left div should span
const computeColSpan = () => {
  nextTick(() => {
    if (leftDiv.value) {
      // Get the grid container
      const gridContainer = leftDiv.value.closest('.container-grid')
      // Get the content element (button or link)
      const contentElement = leftDiv.value.firstElementChild

      if (gridContainer && contentElement) {
        // Get computed styles of the grid container
        const gridStyles = window?.getComputedStyle(gridContainer)

        // if the gridStyles are not available, return
        if (!gridStyles) {
          return
        }

        // Get the width of the contentElement
        const contentWidth = (contentElement as HTMLElement).offsetWidth

        const totalColumns =
          parseFloat(gridStyles.getPropertyValue('--columns')) || 12
        const gridGap =
          parseFloat(gridStyles.getPropertyValue('column-gap')) || 0

        const columnWidth = parseFloat(
          gridStyles.getPropertyValue('grid-template-columns').split(' ')[0],
        )

        // Calculate how many columns the div needs to span
        let columnsToSpan = Math.ceil(
          (contentWidth + gridGap) / (columnWidth + gridGap),
        )

        // Ensure columnsToSpan does not exceed total number of columns
        columnsToSpan = Math.min(columnsToSpan, totalColumns)

        // Set the value of the CSS variable
        leftColSpan.value = columnsToSpan
      }
    }
  })
}

// resize observer for the left div
let resizeObserver: ResizeObserver | undefined

// Initialize ResizeObserver only client-side
if (import.meta.client) {
  // observe the component when it is mounted
  resizeObserver = new ResizeObserver(() => computeColSpan())
}

// disconnect the observer when the component is unmounted
onMounted(() => {
  if (resizeObserver && leftDiv.value) {
    resizeObserver.observe(leftDiv.value)
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})

const hasOneLocations = computed(
  () => props.locations && props.locations.length === 1,
)

const hasMultipleLocations = computed(
  () => props.locations && props.locations.length > 1,
)

const locationsMerged = computed(() =>
  props.locations?.map((v) => v.name).join(', '),
)

// The first location is used for the button link
const firstLink = computed(() => {
  if (props.locations && props.locations.length > 0) {
    if (
      props.locations[0]?.externalLocation &&
      props.locations[0]?.externalLink?.uri?.path
    ) {
      return props.locations[0]?.externalLink?.uri?.path || null
    }

    return props.locations[0]?.page?.url?.path || null
  } else {
    return null
  }
})

const showLinkToMultiple = computed(
  () => props.showStatus && hasMultipleLocations.value,
)

const showLinkToFirst = computed(
  () => props.showStatus && firstLink.value && hasOneLocations.value,
)

defineOptions({
  name: 'Locations',
})
</script>

<style lang="postcss">
.locations__col-span--left {
  grid-column: span var(--col-left-span);
}
.locations__col-span--right {
  grid-column: span calc(var(--columns) - var(--col-left-span));
  grid-template-columns: repeat(
    calc(var(--columns) - var(--col-left-span)),
    minmax(0, 1fr)
  );
}
</style>
